/**
 * studio/src/lib/use-theme.ts
 *
 * Color-scheme preference for the Studio SPA. Defaults to "system"
 * (follow OS light/dark). The user can override to light or dark via
 * Account → Appearance; the choice persists in localStorage.
 *
 * DOM contract:
 *   - "system" → no `data-theme` on <html>; CSS `@media (prefers-color-scheme: dark)` applies
 *   - "light"  → `data-theme="light"`  (forces light even when OS is dark)
 *   - "dark"   → `data-theme="dark"`   (forces dark even when OS is light)
 *
 * An inline script in index.html applies the stored preference before
 * first paint to avoid a flash of the wrong theme.
 */
import { ref, type Ref } from "vue";

export type ColorSchemePreference = "system" | "light" | "dark";
export type EffectiveColorScheme = "light" | "dark";

export const COLOR_SCHEME_STORAGE_KEY = "hp.color-scheme";
export const COLOR_SCHEME_DEFAULT: ColorSchemePreference = "system";

const VALID_PREFERENCES = new Set<ColorSchemePreference>(["system", "light", "dark"]);

function isColorSchemePreference(value: unknown): value is ColorSchemePreference {
  return typeof value === "string" && VALID_PREFERENCES.has(value as ColorSchemePreference);
}

export function readStoredColorScheme(): ColorSchemePreference {
  try {
    const raw = localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
    if (isColorSchemePreference(raw)) return raw;
  } catch {
    /* localStorage unavailable in private modes — fall through */
  }
  return COLOR_SCHEME_DEFAULT;
}

export function writeStoredColorScheme(pref: ColorSchemePreference): void {
  try {
    localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, pref);
  } catch {
    /* swallow — in-memory ref still works */
  }
}

export function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveEffectiveColorScheme(
  pref: ColorSchemePreference,
  prefersDark: boolean = systemPrefersDark(),
): EffectiveColorScheme {
  if (pref === "light") return "light";
  if (pref === "dark") return "dark";
  return prefersDark ? "dark" : "light";
}

/** Apply the user's stored preference to document.documentElement. */
export function applyColorSchemePreference(pref: ColorSchemePreference): EffectiveColorScheme {
  if (typeof document === "undefined") {
    return resolveEffectiveColorScheme(pref);
  }
  const root = document.documentElement;
  if (pref === "system") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", pref);
  }
  return resolveEffectiveColorScheme(pref);
}

let mediaQuery: MediaQueryList | null = null;
let mediaListener: ((event: MediaQueryListEvent) => void) | null = null;
let storageListener: ((event: StorageEvent) => void) | null = null;
let themeInitialized = false;

const sharedPreference = ref<ColorSchemePreference>(COLOR_SCHEME_DEFAULT);
const sharedEffective = ref<EffectiveColorScheme>("light");

function syncEffectiveFromPreference(): void {
  sharedEffective.value = applyColorSchemePreference(sharedPreference.value);
}

function attachSystemListener(): void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
  if (mediaQuery) return;

  mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  mediaListener = () => {
    if (sharedPreference.value === "system") {
      syncEffectiveFromPreference();
    }
  };
  mediaQuery.addEventListener("change", mediaListener);
}


function attachStorageListener(): void {
  if (typeof window === "undefined" || storageListener) return;
  storageListener = (event: StorageEvent) => {
    if (event.key !== COLOR_SCHEME_STORAGE_KEY) return;
    const next = isColorSchemePreference(event.newValue)
      ? event.newValue
      : COLOR_SCHEME_DEFAULT;
    sharedPreference.value = next;
    syncEffectiveFromPreference();
  };
  window.addEventListener("storage", storageListener);
}

/** Call once at app bootstrap (main.ts). Idempotent. */
export function initTheme(): void {
  if (themeInitialized) return;
  themeInitialized = true;

  sharedPreference.value = readStoredColorScheme();
  syncEffectiveFromPreference();
  attachSystemListener();
  attachStorageListener();
}

export interface UseThemeOptions {
  /** Re-read localStorage instead of using the shared ref (tests). */
  reset?: boolean;
}

export interface UseTheme {
  preference: Ref<ColorSchemePreference>;
  effective: Ref<EffectiveColorScheme>;
  setPreference: (next: ColorSchemePreference) => void;
}

export function useTheme(opts: UseThemeOptions = {}): UseTheme {
  if (opts.reset) {
    sharedPreference.value = readStoredColorScheme();
    syncEffectiveFromPreference();
  }

  return {
    preference: sharedPreference,
    effective: sharedEffective,
    setPreference: (next: ColorSchemePreference) => {
      sharedPreference.value = next;
      writeStoredColorScheme(next);
      syncEffectiveFromPreference();
    },
  };
}

/** Inline bootstrap snippet source — keep in sync with index.html. */
export function themeBootstrapScriptSource(): string {
  return `(function(){var k=${JSON.stringify(COLOR_SCHEME_STORAGE_KEY)};var s;try{s=localStorage.getItem(k)}catch(e){s=null}var p=(s==="light"||s==="dark"||s==="system")?s:"system";if(p==="light"||p==="dark"){document.documentElement.setAttribute("data-theme",p)}})();`;
}
